import {
  useEffect, useCallback, useRef, useState,
} from 'react';
import * as signalR from '@microsoft/signalr';
import { LocalStorageService, MessageService } from 'services';
import useLogout from 'hooks/useLogout';
import { useTranslation } from 'react-i18next';
import { AppConstants } from 'constants/index';
import Config from 'config';
import { AuthTokenResponse } from 'app/auth/types';
import { changeTokenState } from 'app/auth/slices';
import { selectAuthTokenState } from 'app/auth/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { HubConnection } from '@microsoft/signalr';

const { isBrRegulation } = Config;

const maxConnectionAttempts = 3;
const url = [AppConstants.api.NotificationAdmin, AppConstants.api.Hubs, AppConstants.api.NotificationHub].join('/');

const useConnection = () => {
  const { logout } = useLogout();
  const { t } = useTranslation();

  const [connection, setConnection] = useState<HubConnection|null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildConnection = useCallback(() => {
    const auth: AuthTokenResponse|null = LocalStorageService.get('auth');
    if (!auth?.access_token || isBrRegulation) return null;
    const silentLogger: signalR.ILogger = {
      log: () => {},
    };

    return new signalR.HubConnectionBuilder()
      .configureLogging(silentLogger)
      .withUrl(`${Config.baseURL}${url}?access_token=${auth.access_token}`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect([0, 2000, 5000])
      .build();
  }, []);

  const startConnection = useCallback(async (conn: HubConnection) => {
    try {
      await conn.start();
      retryCountRef.current = 0;
    } catch (error) {
      const err = error as { statusCode?: number };
      if (err?.statusCode !== 401) {
        if (retryCountRef.current >= maxConnectionAttempts) {
          return;
        }

        retryCountRef.current += 1;

        retryTimeoutRef.current = setTimeout(() => {
          startConnection(conn);
        }, 1000);
      }
    }
  }, []);

  const connect = useCallback(() => {
    const conn = buildConnection();
    if (!conn) return;

    conn.on('ReceiveMessage', (userId, mess) => {
      switch (mess) {
      case 'LogoutOnDisable':
        MessageService.error({
          content: t('notification:accountDisabled'),
          onClose: logout,
        });
        break;

      case 'LogoutOnDelete':
        MessageService.error({
          content: t('notification:accountDeleted'),
          onClose: logout,
        });
        break;
      default:
        break;
      }
    });

    setConnection(conn);
    startConnection(conn);
  }, [buildConnection, logout, startConnection, t]);

  const restartConnection = useCallback(() => {
    retryCountRef.current = 0;
    setConnection(null);
  }, []);

  const dispatch = useAppDispatch();
  const isTokenChanged = useAppSelector(selectAuthTokenState);

  useEffect(() => {
    if (isTokenChanged) {
      dispatch(changeTokenState(false));
      restartConnection();
    }
  }, [dispatch, isTokenChanged, restartConnection]);

  useEffect(() => {
    if (!connection) {
      connect();
    }

    return () => {
      if (retryTimeoutRef?.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (connection) {
        connection.off('ReceiveMessage');
        connection.stop();
      }
    };
  }, [connection, connect]);
};

export default useConnection;
